// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import { clamp } from "@/lib/util";

// models
import { translate } from "@/modules/shared/IntlProviderWrapper";
import * as t from "@/lib/backend/manufacturing2.generated.types";

// subcomponents
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import ReactTable from "@/modules/shared/ui/ReactTable";
import { TableInstance, Row, Column } from "react-table";
import { ReactTableColumn } from "@/modules/shared/ui/ReactTable";
import Loader from "@/modules/shared/ui/Loader";

// backend
import { getLineWeight } from "@/lib/businessLogic";
import { getBackend } from "@/lib/backend/backend2";

// style
import "./LineGroupMatrix.scss";

import img_banner from "@/img/banner.png";

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

type State = {
    linegroup_uuid: string;
    linegroup_title: string;
    error: string;
    warning: string;
    is_loading: boolean;
    processes: IProcessRow[];
    select_all_manual_use_in_planning: boolean;
    select_all_automatic_planning: boolean;
};

enum PROCESS_COLUMNS {
    line_title = "line_title",
    line_uuid = "line_uuid",
    line_external_id = "line_external_id",
    use_in_planning = "use_in_planning",
    manual_use_in_planning = "manual_use_in_planning",
    material_uuid = "material_uuid",
    material_external_id = "material_external_id",
    material_title = "material_title",
    process_num = "process_num",
    process_title = "process_title",
    process_uuid = "process_uuid",
    share_quantity = "share_quantity",
    is_most_common = "is_most_common",
    neccessary_value = "neccessary_value",
}

export type IProcessRow = {
    line_title: string;
    line_uuid: string;
    line_external_id: string;
    use_in_planning: boolean;
    manual_use_in_planning: boolean;
    material_uuid: string;
    material_external_id: string;
    material_title: string;
    process_num: string;
    process_title: string;
    process_uuid: string;
    share_quantity: number;
    is_most_common: boolean;
    neccessary_value: boolean;
};

const PercentageCell = (props: TableInstance): string => {
    const value: number = props.value;
    return Math.round(value) + "%";
};
const ProcessNumberCell = (props: TableInstance): string => {
    const value: string = props.value;
    return value === "null" ? "-" : value;
};

const sortString = (row_a: Row<IProcessRow>, row_b: Row<IProcessRow>, column_id: string): number => {
    const a: string = row_a.values[column_id].toLowerCase();
    const b: string = row_b.values[column_id].toLowerCase();
    return a === b ? 0 : a > b ? 1 : -1;
};

/** Convert strings to numbers or null - prepare for filter */
const stringToMaybeNumber = (value: string): number | null => {
    if (value === "") {
        return null;
    }

    // Clamp the value to [0, 100] since manual input can exceed min/max attributes.
    return clamp(parseInt(value), 0, 100);
};

/** Convert number to string or empty string if null */
const maybeNumberToString = (value: number | null): string => {
    if (value == null) {
        return "";
    }

    return String(value);
};

const RangeColumnFilter = ({ column }: Column<IProcessRow>) => {
    /** Converts share range strings to numbers or null and updates filter state */
    const handleShareChange = ([min, max]: [string, string]): void => {
        setFilter([min, max].map(stringToMaybeNumber));
    };

    /** Validate input on blur: make sure it's a number and reset if min > max */
    const handleOnBlur = ([min, max]: [string, string]): void => {
        const [min_num, max_num] = [min, max].map(stringToMaybeNumber);

        if (min_num == null) {
            return handleShareChange(["0", max]);
        }

        if (max_num == null) {
            return handleShareChange([min, "100"]);
        }
    };

    const { filterValue = [0, 100], setFilter } = column;
    // Convert filterValue to strings for input fields
    const [min_value, max_value] = filterValue.map(maybeNumberToString);

    return (
        <div className="d-flex align-items-center column-filter">
            <input
                type="number"
                min="0"
                max="100"
                value={min_value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleShareChange([e.target.value, max_value])}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => handleOnBlur([e.target.value, max_value])}
                placeholder={translate("common.min", "Min")}
                className="form-control form-control-sm mr-1"
            />
            <span className="mx-1">-</span>
            <input
                type="number"
                min="0"
                max="100"
                value={max_value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleShareChange([min_value, e.target.value])}
                onBlur={(e: React.ChangeEvent<HTMLInputElement>) => handleOnBlur([min_value, e.target.value])}
                placeholder={translate("common.max", "Max")}
                className="form-control form-control-sm ml-1"
            />
        </div>
    );
};

/**
 * Form to manage production matrix for materials and lines in the line group
 */
class LineGroupMatrix extends Component<Props, State> {
    state: State = {
        linegroup_uuid: "",
        linegroup_title: "",
        error: "",
        warning: "",
        is_loading: true,
        processes: [],
        select_all_manual_use_in_planning: false,
        select_all_automatic_planning: false,
    };

    async componentDidMount() {
        const { uuid } = this.props.match.params;
        const { line_groups } = await getBackend().manufacturing.getLineGroups({});
        const line_group = line_groups.filter(x => x.uuid === uuid)[0];

        if (line_group !== undefined) {
            try {
                const { lines } = await getBackend().manufacturing.getLineGroupMatrix({ linegroup_uuid: uuid! });
                if (lines.length === 0) {
                    this.setState({ is_loading: false });
                    return null;
                }

                lines.sort((l1, l2) => getLineWeight(l1.line_uuid) - getLineWeight(l2.line_uuid));

                const processDetailRows = this.setRows(lines);

                this.setState({
                    linegroup_uuid: uuid!,
                    linegroup_title: line_group.title,
                    processes: processDetailRows,
                    is_loading: false,
                });
            } catch (error) {
                this.setState({
                    error: translate("common.error", "An error occured while fetching data"),
                    is_loading: false,
                });
            }
        } else {
            this.setState({ error: `Unknown line-group '${uuid}'`, is_loading: false });
        }
    }

    renderUseInPlanning = (props: TableInstance) => {
        const line_uuid: string = props.row.original.line_uuid;
        const material_uuid: string = props.row.original.material_uuid;
        const process_uuid: string = props.row.original.process_uuid;
        const value: boolean = props.value;
        return (
            <label
                className="mb-0"
                title={translate(
                    "Manufacturing.LineGroups.matrix.use_in_planning",
                    "Process can be used as alternative in the automatic planning process"
                )}
            >
                <i className="fas fa-magic align-middle" />
                <input
                    type="checkbox"
                    name="enabled"
                    className="align-middle ml-1"
                    checked={value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        this.handleEnabled(event, line_uuid, material_uuid, process_uuid);
                    }}
                />
            </label>
        );
    };

    renderManualUseInPlanning = (props: TableInstance) => {
        const line_uuid: string = props.row.original.line_uuid;
        const material_uuid: string = props.row.original.material_uuid;
        const process_uuid: string = props.row.original.process_uuid;
        const value: boolean = props.value;
        return (
            <label
                className="mb-0 ml-2"
                title={translate(
                    "Manufacturing.LineGroups.matrix.manual_use_in_planning",
                    "Process can be used in manual mode on planning table"
                )}
            >
                <i className="fas fa-hand-paper align-middle" />
                <input
                    type="checkbox"
                    name="enabled"
                    className="align-middle ml-1"
                    checked={value}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        this.handleManualEnabled(event, line_uuid, material_uuid, process_uuid);
                    }}
                />
            </label>
        );
    };

    renderSelectHeader = (table: TableInstance, selectFunction: Function, title: string) => {
        const rows: Row[] = table.filteredRows || [];

        return (
            <div className="d-flex flex-column select-buttons">
                <span>{title}</span>
                <div>
                    <button
                        className="btn btn-primary"
                        title={translate("common.deselect_all", "Deselect all")}
                        onClick={() => selectFunction(rows, false)}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                    <button
                        className="btn btn-primary"
                        title={translate("common.select", "Select")}
                        onClick={() => selectFunction(rows, true)}
                    >
                        <i className="fas fa-check"></i>
                    </button>
                </div>
            </div>
        );
    };

    rangeFilter = (rows: Row[], id: string, [min, max]: [number, number]): Row[] => {
        if (min == null || max == null) {
            return [];
        }

        return rows.filter(row => {
            const cell_value = row.values[id];

            // Handle edge cases
            if (cell_value == null) {
                return false;
            }

            return min <= cell_value && cell_value <= max;
        });
    };

    getTableColumns = (): ReactTableColumn<IProcessRow>[] => {
        return [
            {
                Header: "",
                accessor: PROCESS_COLUMNS.line_title,
                filterInputPlaceholder: translate("common.line", "Line"),
                enableSorting: true,
                sortType: sortString,
            },
            {
                Header: "",
                accessor: PROCESS_COLUMNS.line_external_id,
                filterInputPlaceholder: translate("OrderTableProduction.line_external_id", "Line ID"),
                enableSorting: true,
                sortType: sortString,
            },
            {
                Header: (table: TableInstance) =>
                    this.renderSelectHeader(
                        table,
                        this.handleSelectAllUseInPlanning,
                        translate(
                            "tag_groups.LINE_GROUP_TAGS.values.title.automatic_scheduling",
                            "Automatic scheduling"
                        )
                    ),
                accessor: PROCESS_COLUMNS.use_in_planning,
                disableFilters: true,
                enableSorting: false,
                Cell: this.renderUseInPlanning,
            },
            {
                Header: (table: TableInstance) =>
                    this.renderSelectHeader(
                        table,
                        this.handleSelectAllManualUseInPlanning,
                        translate("Manufacturing.LineGroups.matrix.manual_scheduling", "Manual scheduling")
                    ),
                accessor: PROCESS_COLUMNS.manual_use_in_planning,
                disableFilters: true,
                enableSorting: false,
                Cell: this.renderManualUseInPlanning,
            },
            {
                Header: "",
                accessor: PROCESS_COLUMNS.material_external_id,
                filterInputPlaceholder: translate("OrderTableProduction.material_external_id", "Material ID"),
                enableSorting: true,
                sortType: sortString,
            },
            {
                Header: "",
                accessor: PROCESS_COLUMNS.material_title,
                filterInputPlaceholder: translate("OrderTableProduction.material_title", "Material title"),
                enableSorting: true,
                sortType: sortString,
            },
            {
                Header: translate("common.process_num", "Process number"),
                accessor: PROCESS_COLUMNS.process_num,
                disableFilters: true,
                enableSorting: true,
                sortType: sortString,
                Cell: ProcessNumberCell,
            },
            {
                Header: "",
                accessor: PROCESS_COLUMNS.process_title,
                filterInputPlaceholder: translate("common.process", "Process"),
                enableSorting: true,
            },
            {
                Header: `${translate("common.share", "Share")} (%)`,
                accessor: PROCESS_COLUMNS.share_quantity,
                Filter: RangeColumnFilter,
                filter: this.rangeFilter,
                enableSorting: true,
                sortType: "basic",
                Cell: PercentageCell,
            },
            {
                Header: (table: TableInstance) => (
                    <button
                        className="btn btn-secondary clear-filters"
                        title={translate("common.clear_filters", "Clear filters")}
                        onClick={() => {
                            table.setAllFilters([]);
                        }}
                    >
                        <i className="icon icon-filter-circle-xmark-solid"></i>
                    </button>
                ),
                accessor: PROCESS_COLUMNS.neccessary_value,
                disableFilters: true,
                Cell: () => <div />,
            },
        ];
    };

    handleEnabled = (
        event: React.ChangeEvent<HTMLInputElement>,
        line_uuid: string,
        material_uuid: string,
        process_uuid: string
    ) => {
        const target = event.target;
        if (target.type === "checkbox") {
            let processes = this.state.processes;
            const proc_idx = processes.findIndex(
                proc =>
                    proc.line_uuid === line_uuid &&
                    proc.material_uuid === material_uuid &&
                    proc.process_uuid === process_uuid
            );
            processes[proc_idx].use_in_planning = target.checked;

            if (target.checked) {
                processes[proc_idx].manual_use_in_planning = target.checked;
            }

            this.setState({ processes });
        }
    };

    handleManualEnabled = (
        event: React.ChangeEvent<HTMLInputElement>,
        line_uuid: string,
        material_uuid: string,
        process_uuid: string
    ) => {
        const target = event.target;
        if (target.type === "checkbox") {
            let processes = this.state.processes;
            const proc_idx = processes.findIndex(
                proc =>
                    proc.line_uuid === line_uuid &&
                    proc.material_uuid === material_uuid &&
                    proc.process_uuid === process_uuid
            );
            processes[proc_idx].manual_use_in_planning = target.checked;

            if (!target.checked) {
                processes[proc_idx].use_in_planning = target.checked;
            }

            this.setState({ processes });
        }
    };

    handleSubmit = () => {
        // assamble list of processes to add and remove from the matrix
        const enable_process_uuids: string[] = [];
        const disable_process_uuids: string[] = [];
        const enable_manual_process_uuids: string[] = [];
        const disable_manual_process_uuids: string[] = [];
        let processes = this.state.processes;
        for (const process of processes) {
            if (process.use_in_planning) {
                enable_process_uuids.push(process.process_uuid);
            } else {
                disable_process_uuids.push(process.process_uuid);
            }

            if (process.manual_use_in_planning) {
                enable_manual_process_uuids.push(process.process_uuid);
            } else {
                disable_manual_process_uuids.push(process.process_uuid);
            }
        }

        // submit lists to the backend
        getBackend()
            .manufacturing.setLineGroupMatrix({
                disable_process_uuids,
                enable_process_uuids,
                disable_manual_process_uuids,
                enable_manual_process_uuids,
                linegroup_uuid: this.state.linegroup_uuid,
            })
            .then(() => {
                // success, go back to linegroup details page
                this.props.history.push("/digital-twin/resources/line_groups/" + encodeURI(this.state.linegroup_uuid));
            })
            .catch(err => {
                // error, report and stay here
                this.setState({ error: `${err}` });
            });
    };

    handleSelectMax = () => {
        let processes = this.state.processes;
        for (const proc of processes) {
            if (proc.is_most_common) {
                proc.use_in_planning = true;
                proc.manual_use_in_planning = true;
            }
        }

        this.setState({
            processes: processes,
        });
    };

    handleSelectAllUseInPlanning = (rows: Row[], checked: boolean) => {
        const processes = this.state.processes;

        for (const row of rows) {
            const process = row.original;
            const process_uuid = process.process_uuid;
            const proc_idx = processes.findIndex(proc => proc.process_uuid === process_uuid);
            processes[proc_idx].use_in_planning = checked;
        }
        this.setState({
            processes: processes,
            select_all_automatic_planning: checked,
        });
    };

    handleSelectAllManualUseInPlanning = (rows: Row[], checked: boolean) => {
        const processes = this.state.processes;

        for (const row of rows) {
            const process = row.original;
            const process_uuid = process.process_uuid;
            const proc_idx = processes.findIndex(proc => proc.process_uuid === process_uuid);

            processes[proc_idx].manual_use_in_planning = checked;
        }
        this.setState({
            processes: processes,
            select_all_manual_use_in_planning: checked,
        });
    };

    handleCancel = () => {
        this.props.history.push("/digital-twin/resources/line_groups/" + encodeURI(this.state.linegroup_uuid));
    };

    handleRowClassName = (props: Row<IProcessRow>): string[] => {
        const is_most_common: boolean = props.original.is_most_common;
        const manual_use_in_planning: boolean = props.original.manual_use_in_planning;
        const use_in_planning: boolean = props.original.use_in_planning;
        const row_classes: string[] = [];

        if (is_most_common) {
            row_classes.push("process_table_max");
        }

        if (!manual_use_in_planning && !use_in_planning) {
            row_classes.push("process_table_new");
        }

        return row_classes;
    };

    setRows(lines: t.ILineGroupMatrixLine[]) {
        const processRows: IProcessRow[] = [];

        for (let i = 0; i < lines.length; i++) {
            const line_title = lines[i].line_title;
            const line_uuid = lines[i].line_uuid;
            const line_external_id = lines[i].line_external_id;

            // Sorts linegroup materials in alphabetical order
            const materials: t.ILineGroupMatrixMaterial[] = [
                ...lines[i].materials.sort((m1, m2) => m1.material_title.localeCompare(m2.material_title)),
            ];
            for (let j = 0; j < materials.length; j++) {
                const material_external_id = materials[j].material_external_id;
                const material_uuid = materials[j].material_uuid;

                // Remembers material name
                const material_title = materials[j].material_title;

                // Sorts processes by their importance
                let processes = [...materials[j].processes.sort((p1, p2) => p2.quantity_sum - p1.quantity_sum)];

                for (let k = 0; k < processes.length; k++) {
                    // Finds process details in state (from api call)
                    const process_uuid = processes[k].process_uuid;

                    processRows.push({
                        line_title,
                        line_uuid,
                        line_external_id,
                        use_in_planning: processes[k].use_in_planning,
                        manual_use_in_planning: processes[k].manual_use_in_planning,
                        material_uuid,
                        material_external_id,
                        material_title,
                        process_uuid,
                        process_num: processes[k].process_num,
                        process_title: processes[k].process_title,
                        share_quantity: Math.round(processes[k].share_quantity * 100),
                        is_most_common: processes[k].is_most_common,
                        neccessary_value: false,
                    });
                }
            }
        }
        return processRows;
    }

    render() {
        const showContent =
            ((this.state.error === "" && this.state.warning === "") || this.state.processes.length > 0) &&
            !this.state.is_loading;
        return (
            <div key="ds_new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    <h5>{this.state.linegroup_title}</h5>
                                    {this.state.is_loading && <Loader />}
                                    {showContent && (
                                        <ReactTable
                                            data={this.state.processes}
                                            columns={this.getTableColumns()}
                                            isSticky={true}
                                            rowClassName={this.handleRowClassName}
                                            className="table-responsive"
                                            initialState={{
                                                sortBy: [
                                                    {
                                                        id: PROCESS_COLUMNS.line_title,
                                                        desc: false,
                                                    },
                                                ],
                                            }}
                                        />
                                    )}

                                    <ErrorComponent msg={this.state.error} type="error" />
                                    <ErrorComponent msg={this.state.warning} type="warning" />

                                    <div className="button-list order-last ml-auto">
                                        {this.state.processes.length > 0 && (
                                            <button className="btn btn-outline-primary" onClick={this.handleSubmit}>
                                                <FormattedMessage id="common.save" defaultMessage="Save" />
                                            </button>
                                        )}
                                        {showContent && (
                                            <button
                                                className="btn btn-outline-secondary"
                                                onClick={this.handleSelectMax}
                                            >
                                                <FormattedMessage
                                                    id="Manufacturing.LineGroups.matrix.select_max"
                                                    defaultMessage="Select most common"
                                                />
                                            </button>
                                        )}
                                        <button className="btn btn-outline-secondary" onClick={this.handleCancel}>
                                            <FormattedMessage id="common.back" defaultMessage="Back" />
                                        </button>
                                    </div>
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

export default LineGroupMatrix;
